// src/com/store_info/StoreInfoForStore.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { Button } from '@mui/material';
import { styled  } from '@mui/material/styles';
import {ContentConainer, ContentHeader, ContentBody, ControlBox, ToolBox} from '../../common/TopViewControl';
import {GET_STORE_INFO, EDIT_STORE_INFO_PEOPLE} from './StoreInfoGql';
import { StoreInfoDictionary } from './StoreInfoDict';
import { useAlertSetter } from '../../api/app-hook';
import { useSnackbar } from 'notistack';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { storeInfoItemRepo, userInfoRepo } from '../../value/AppModel';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import StoreHouseSvg from '../../asset/image/store_house.svg';
import { VzmInputBlock, VzmPrint } from '../../common/VzmStyled';
import PhoneField from '../../common/PhoneField';
import InputUtil from '../../value/InputUtil';
import VzmTextField from '../../common/VzmTextField';

const InnerBox = styled(Box)({display:'flex', height: '100%'});
const InputColumn = styled(Box)({width: 400});
const SideColumn = styled(Box)({flexGrow: 1});

const InputMap = StoreInfoDictionary.peopleInputMap;
const dot5 = '.....';
const ValueColor = '#557';

export default function StoreInfoForStore() {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const storeInfoData = useReactiveVar(storeInfoItemRepo); // {when:0, data:{}}
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const openRef = useRef(false);
    const lastReloadRef = useRef(0);
    const [getStoreInfoItem, responseItem] = useLazyQuery(GET_STORE_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetItem(data, option)},
        onError: (error) => addAlert({title: StoreInfoDictionary.errorTitle.Get, message: [error.message]})
    });
    const [editStoreInfoPeople, responseEdit] = useMutation( EDIT_STORE_INFO_PEOPLE, {
        onCompleted: (data, option) => onCompleteEditPeople(data, option),
        onError: (error) => addAlert({title: StoreInfoDictionary.errorTitle.Edit, message: [error.message]})
    } );

    const getStoreInfoNow = () => {
        getStoreInfoItem({variables: {storeId: sessionInfo.user.dscId}});
    };

    useEffect(()=>{
        if(!openRef.current) {
            openRef.current = true;
            getStoreInfoNow();
        }
    }, []);
    
    useEffect(()=>{
        if(storeInfoData.data.storeId) {
            // emptyInputData: {data:{}, error:{}, hasError:false, changed: false, errorLabels:[]},
            const inputSet = {...AppObject.emptyInputData};
            inputSet.data = {...storeInfoData.data};
            setInput(inputSet);
        }
    }, [storeInfoData]);

    const onCompleteGetItem = (data, option) => {
        if(data.storeInfoItem) {
            storeInfoItemRepo({when: Date.now()/1000, data: data.storeInfoItem});
            lastReloadRef.current = new Date().getTime()/1000;
        }
        else addAlert({title:StoreInfoDictionary.errorTitle.Get, message:['점포정보를 찾을 수 없습니다.']});
    };
    
    const onCompleteEditPeople = (data, clientOption) => {
        if(data.storeInfoEditPeople.ok) {
            enqueueSnackbar('점포 연락처를 수정했습니다.', {variant:'success', autoHideDuration:1600});
            getStoreInfoNow();
        }
        else addAlert({title:StoreInfoDictionary.errorTitle.Edit, message:[data.storeInfoEditPeople.message]});
    };
    
    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, true);
        setInput(newInput);
    };

    const onClickSave = () => {
        const inputData = {
            storeId: input.data.storeId,
            officeTelno: input.data.officeTelno,
            storeStaffName: input.data.storeStaffName,
            secuTelno: input.data.secuTelno,
            secuStaffName: input.data.secuStaffName
        };
        editStoreInfoPeople({variables: {storeInfo: inputData}});
    };

    const onClickRead = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff > 60) getStoreInfoNow();
        else
            enqueueSnackbar(AppWord.ReloadNotInOneMinute, {variant:'info', autoHideDuration:2000});
    };

    const loadingAny = responseItem.loading || responseEdit.loading;

    return(
        <ContentConainer>
            <ContentHeader>
                <ControlBox style={{justifyContent:'center', padding:10, color: AppStyle.PrimaryRGB}}>
                    <span style={{
                        fontSize:'0.9rem', border: '1px solid '+AppStyle.PrimaryRGB, 
                        padding:'5px 20px', borderRadius:20
                        }}>
                        우리 점포의 연락처를 수정할 수 있습니다. 정보가 정확한지 확인해 주세요.
                    </span>
                            <Button
                                variant={AppStyle.mui.contained}
                                color={AppStyle.mui.primary}
                                style={{marginLeft:30}}
                                onClick={onClickSave}
                                disabled={loadingAny || !input.changed || input.hasError}
                            >
                                저장
                            </Button>
                            <Button
                                variant={AppStyle.mui.contained}
                                color={AppStyle.mui.success}
                                style={{marginLeft:10}}
                                onClick={onClickRead}
                                disabled={loadingAny || !input.changed}
                            >
                                다시 읽기
                            </Button>
                </ControlBox>
            </ContentHeader>
            <ContentBody>
                <InnerBox>
                    <SideColumn>
                        &nbsp;
                    </SideColumn>
                    <InputColumn>
                        <VzmPrint label={InputMap.storeId.label}
                            value={input.data.storeId || dot5}
                            color={ValueColor}
                        />
                        
                        <VzmPrint label={InputMap.storeName.label}
                            value={input.data.storeName || dot5}
                            color={ValueColor}
                        />

                        <VzmPrint label={InputMap.zipCd.label}
                            value={input.data.zipCd || dot5}
                            color={ValueColor}
                        />
                        
                        <VzmPrint label={InputMap.addr.label}
                            value={input.data.addr || dot5}
                            color={ValueColor}
                        />

                        <VzmInputBlock style={{marginTop:20}}>
                            <VzmTextField
                                dict={InputMap.storeStaffName}
                                value={input.data.storeStaffName}
                                onChange={onChangeValue}
                                fullWidth={true}
                            />
                        </VzmInputBlock>
                        <VzmInputBlock>
                            <PhoneField
                                dict={InputMap.officeTelno}
                                value={input.data.officeTelno}
                                onChange={onChangeValue}
                                fullWidth={true}
                            />
                        </VzmInputBlock>
                        <VzmInputBlock>
                            <VzmTextField
                                dict={InputMap.secuStaffName}
                                value={input.data.secuStaffName}
                                onChange={onChangeValue}
                                fullWidth={true}
                            />
                        </VzmInputBlock>
                        <VzmInputBlock>
                            <PhoneField
                                dict={InputMap.secuTelno}
                                value={input.data.secuTelno}
                                onChange={onChangeValue}
                                fullWidth={true}
                            />
                        </VzmInputBlock>
                    </InputColumn>
                    <SideColumn>
                        <img src={StoreHouseSvg}
                            alt={input.data.storeName || '...Wait...'} width={'50%'}
                            style={{marginLeft: '5%', marginTop: '5%'}}
                        />
                    </SideColumn>
                </InnerBox>
            </ContentBody>
        </ContentConainer>
    );
}